/**
 * Cart Item block entry.
 *
 * The − and + are a convenience over a number field that already works: the
 * field is inside the cart's form, so typing a figure and pressing the cart's
 * update button does the same thing with the script blocked.
 *
 * All this block does is step the figure and say so. Saving it is the cart's
 * errand, not one line's — the totals beside the list change too — so the news
 * goes out as an event and the cart block decides what to do about it.
 */

import './index.scss';

/**
 * Step one line's quantity, keeping it at one or more.
 *
 * @param {HTMLElement} button The − or + that was pressed.
 * @returns {void}
 */
function step(button) {
    const field = button.closest('.cart-item__quantity')?.querySelector('.cart-item__count');

    if (!field) {
        return;
    }

    const by = Number(button.dataset.step ?? 0);
    const next = Math.max(1, Number(field.value || 1) + by);

    if (next === Number(field.value)) {
        return;
    }

    field.value = String(next);

    /*
     * `change`, and only `change`. It is what a field emits when a person edits
     * it, so anything already watching this page the ordinary way — the cart's
     * own script, a plugin — hears the button press as if it were typing, and
     * the listener below turns it into the cart's own news exactly once.
     */
    field.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('click', (event) => {
    const button = event.target.closest('.cart-item__step');

    if (button) {
        step(button);
    }
});

/*
 * The one place a quantity change is announced, whether it came from the
 * buttons above, from typing, or from the browser's own spinner. Emitting it
 * from the stepper as well would have it announced twice — and, since the
 * stepper announces by dispatching `change`, would have this listener call
 * itself until the stack ran out.
 */
document.addEventListener('change', (event) => {
    if (!event.target.matches('.cart-item__count')) {
        return;
    }

    document.dispatchEvent(
        new CustomEvent('cart:quantity', {
            detail: {
                quantity: Number(event.target.value || 1),

                /* Which line, so the cart can redraw that card and leave the
                 * rest of the list — and the reader's place in it — alone. */
                key: event.target.closest('.cart-item')?.dataset.key ?? '',
            },
        })
    );
});

/*
 * ---- changing the choices where they were made ----
 *
 * "Edit selection" is a link to the product page and stays one: without script
 * that is the only way to change a colour, and it works. With script it opens
 * the panel already in the card instead, which saves a page load and a scroll
 * back to a cart the reader had already read.
 */

/**
 * Open or close one line's editor.
 *
 * @param {HTMLElement} toggle The "Edit selection" control.
 * @param {boolean}     open   Whether it should be open.
 * @returns {void}
 */
function editor(toggle, open) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls') ?? '');
    const card = toggle.closest('.cart-item');

    if (!panel || !card) {
        return;
    }

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    /*
     * Everything at once. The two sides used to cross-fade, with the height
     * following a beat later so the list did not jump under a face that was
     * still readable — but with nothing fading there is no face left to protect,
     * and a panel that opens the instant it is asked for beats one that arrives.
     */
    panel.hidden = !open;
    card.toggleAttribute('data-editing', open);

    if (open) {
        remember(panel);
        panel.querySelector('.cart-item__swatch-input:not(:disabled)')?.focus();
    }
}

/**
 * Note what a panel answered when it opened.
 *
 * Cancel has to put the card back as it was, and "as it was" is not something
 * the radios still know once they have been clicked — a radio remembers only
 * the last choice, not the one it was rendered with.
 *
 * @param {HTMLElement} panel The editor.
 * @returns {void}
 */
function remember(panel) {
    for (const input of panel.querySelectorAll('.cart-item__swatch-input')) {
        input.dataset.was = input.checked ? 'yes' : 'no';
    }
}

/**
 * Put a panel back to what it answered when it opened.
 *
 * @param {HTMLElement} panel The editor.
 * @returns {void}
 */
function restore(panel) {
    for (const input of panel.querySelectorAll('.cart-item__swatch-input')) {
        const was = 'yes' === input.dataset.was;

        if (input.checked !== was) {
            input.checked = was;
        }

        /* The legend follows the tiles, so it is told rather than left saying
         * whatever the abandoned choice was. */
        if (was) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}

document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.cart-item__edit[data-edit]');

    if (toggle) {
        /* Only now is the link not needed: the panel it opens is on this page. */
        event.preventDefault();
        editor(toggle, toggle.getAttribute('aria-expanded') !== 'true');
        return;
    }

    const cancel = event.target.closest('[data-cancel]');

    if (cancel) {
        const card = cancel.closest('.cart-item');
        const back = card?.querySelector('.cart-item__edit[data-edit]');
        const panel = card?.querySelector('.cart-item__editor');

        if (panel) {
            restore(panel);
        }

        if (back) {
            editor(back, false);
            back.focus();
        }

        return;
    }

    const save = event.target.closest('[data-save]');

    if (save) {
        commit(save.closest('.cart-item'));
    }
});

/**
 * Send the chosen values for one line, and let the cart redraw itself.
 *
 * @param {HTMLElement|null} card The line being changed.
 * @returns {void}
 */
function commit(card) {
    if (!card) {
        return;
    }

    const attributes = {};

    for (const input of card.querySelectorAll('.cart-item__swatch-input:checked')) {
        attributes[`attribute_${input.dataset.swatch}`] = input.value;
    }

    document.dispatchEvent(
        new CustomEvent('cart:variation', {
            detail: { key: card.dataset.key ?? '', attributes },
        })
    );
}

/*
 * The legend follows the tiles. Picking a colour with no name beside it leaves
 * the reader to work out which of twenty-five squares is now ringed, and the
 * product page does not ask that of them either.
 */
document.addEventListener('change', (event) => {
    const input = event.target.closest('.cart-item__swatch-input');

    if (!input) {
        return;
    }

    const axis = input.closest('.cart-item__axis');
    const value = axis?.querySelector(
        `[data-axis-value="${CSS.escape(input.dataset.swatch ?? '')}"]`
    );

    if (!value) {
        return;
    }

    /* The name the server already printed beside this tile, rather than one
     * assembled here from a slug. */
    const name =
        input
            .closest('.cart-item__swatch')
            ?.querySelector('.cart-item__swatch-name, .screen-reader-text')?.textContent ?? '';

    value.textContent = name.trim();
});

/**
 * Dim what the chosen weave does not carry, and the weaves that lack the chosen
 * colour — the same rule the product page applies, applied here too.
 *
 * Nothing is disabled: picking a dimmed tile is a real choice that moves the
 * other axis with it, exactly as it does on the product page. The panel carries
 * the fabric-to-colour map because a cart line has no variation payload of its
 * own to work it out from.
 *
 * @param {HTMLElement} editor The open edit panel.
 * @returns {void}
 */
function mark(editor) {
    let pairs = {};

    try {
        pairs = JSON.parse(editor.dataset.pairs ?? '{}');
    } catch {
        return;
    }

    if (!Object.keys(pairs).length) {
        return;
    }

    const fabric = editor.querySelector('[data-swatch="pa_fabric"]:checked')?.value ?? '';
    const colour = editor.querySelector('[data-swatch="pa_color"]:checked')?.value ?? '';
    const here = new Set(pairs[fabric] ?? []);

    for (const tile of editor.querySelectorAll('[data-swatch="pa_color"]')) {
        tile.closest('.cart-item__swatch')?.toggleAttribute('data-other', !here.has(tile.value));
    }

    for (const tile of editor.querySelectorAll('[data-swatch="pa_fabric"]')) {
        const carries = (pairs[tile.value] ?? []).includes(colour);

        tile.closest('.cart-item__swatch')?.toggleAttribute('data-other', !carries);
    }
}

document.addEventListener('change', (event) => {
    const editor = event.target.closest?.('.cart-item__editor');

    if (editor) {
        mark(editor);
    }
});

for (const editor of document.querySelectorAll('.cart-item__editor')) {
    mark(editor);
}
